import { useMatches } from "react-router";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import { createRecsOptionsSchema } from "~/hec/builder/carousel-options/carousel-options.schemas";
import { useBuilderOptions } from "~/hec/builder/carousel-options/use-builder-options";
import { ProductRecommendations } from "~/hec/components/recs/product-recommendations";

import { recsOptionInputs } from "./inputs";
import type {
  LegacyProductRecommendationsBuilderProps,
  ProductRecommendationsBuilderProps,
} from "./types";

const ProductRecommendationsBuilder = ({
  podId,
  itemIds: itemIdsInput,
  groupId,
  attributes,
  builderBlock,
  ...options
}: ProductRecommendationsBuilderProps) => {
  const recsOptions = useBuilderOptions(
    createRecsOptionsSchema,
    options,
    builderBlock,
  );
  // Dynamic seed: when placed on a PDP, default itemIds to the current
  // product. Authors can override by supplying itemIds in Builder.
  const matches = useMatches();
  const dynamicProductId = matches.find(
    m => (m.params as { productId?: string })?.productId,
  )?.params.productId as string | undefined;

  const staticItemIds = itemIdsInput
    ? itemIdsInput
        .split(",")
        .map(s => s.trim())
        .filter(Boolean)
    : undefined;
  const itemIds =
    staticItemIds ?? (dynamicProductId ? [dynamicProductId] : undefined);

  if (!podId) return null;
  return (
    <div {...(attributes ?? {})}>
      <ProductRecommendations
        podId={podId}
        itemIds={itemIds}
        groupId={groupId}
        {...recsOptions}
      />
    </div>
  );
};

export const ProductRecommendationsDefinition = {
  component: ProductRecommendationsBuilder,
  name: "productRecommendations",
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  inputs: [
    {
      name: "podId",
      type: "string",
      required: true,
      helperText:
        "CIO pod identifier (e.g. 'home_page_1'). Configured in BM content asset.",
    },
    {
      name: "itemIds",
      type: "string",
      helperText:
        "Comma-separated seed product IDs. Required by strategies like complementary, alternative, similar items, and bundles. Leave blank on a PDP to auto-seed with the current product; leave blank elsewhere for bestsellers/popular pods.",
    },
    {
      name: "groupId",
      type: "string",
      helperText:
        "CIO group_id (category) filter. Use for category-scoped recommendations.",
    },
    ...recsOptionInputs,
  ],
} satisfies RegisteredComponent;

/**
 * Legacy "Product Recommendations" block (SFCC-11794).
 *
 * hec-composable (MRT) registered this Builder block under the Title-Case name
 * "Product Recommendations" with a different option shape than the Workers
 * `productRecommendations` block above. HSA Learning Center article entries were
 * authored against that legacy schema and store, e.g.
 *   { isCategory: true, categoryId: "best-sellers", podId: "", slidesVisible: {…} }
 * On Workers those entries matched no registered component (the SDK
 * "Could not find a registered component named \"Product Recommendations\""
 * warning) and rendered nothing — the recs section was missing on article pages.
 *
 * This additive, registry-only alias (same pattern as the deprecated
 * Carousel / ThemeBox entries) lets legacy entries resolve. MRT drove both a
 * category (browse) and a pod (recommendations) path through ONE component with
 * ONE visual style — only the CIO data source differed. We preserve that here by
 * routing both branches through the canonical <ProductRecommendations> carousel
 * (the same component the PDP uses), selecting the data source by `isCategory`:
 *   - isCategory + categoryId → CIO browse by group_id (via the component's
 *     `categoryId` prop), heading from the browse group's display_name.
 *   - podId (non-category)    → the CIO recommendations pod.
 * This gives article-page recs the same tiles / heading bar / navigation as the
 * PDP recs, matching MRT (SFCC-11794), and the same Builder options (COM-426:
 * recsOptionInputs / createRecsOptionsSchema). New content should author the
 * `productRecommendations` (pod) or `ProductCarousel` (category) blocks directly;
 * this exists only for backward-compat with entries migrated from MRT.
 */
const LegacyProductRecommendationsBuilder = ({
  isCategory,
  categoryId,
  podId,
  groupId,
  attributes,
  builderBlock,
  ...options
}: LegacyProductRecommendationsBuilderProps) => {
  const recsOptions = useBuilderOptions(
    createRecsOptionsSchema,
    options,
    builderBlock,
  );

  if (isCategory && categoryId) {
    return (
      <div {...(attributes ?? {})}>
        <ProductRecommendations categoryId={categoryId} {...recsOptions} />
      </div>
    );
  }

  if (podId) {
    return (
      <div {...(attributes ?? {})}>
        <ProductRecommendations
          podId={podId}
          groupId={groupId}
          {...recsOptions}
        />
      </div>
    );
  }

  return null;
};

export const ProductRecommendationsLegacyDefinition = {
  component: LegacyProductRecommendationsBuilder,
  name: "Product Recommendations",
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  inputs: [
    {
      name: "isCategory",
      type: "boolean",
      defaultValue: false,
      helperText:
        "Category (CIO browse by group_id) vs pod (CIO recommendations).",
    },
    {
      name: "categoryId",
      type: "string",
      helperText: "CIO group_id / category id (e.g. 'best-sellers').",
      showIf: `options.get('isCategory')`,
    },
    {
      name: "podId",
      type: "string",
      helperText: "CIO recommendations pod id (e.g. 'pdp-2').",
      showIf: `!options.get('isCategory')`,
    },
    {
      name: "groupId",
      type: "string",
      helperText: "Optional CIO group_id filter for the pod path.",
      showIf: `!options.get('isCategory')`,
    },
    {
      name: "addedToCartPodId",
      type: "string",
      helperText:
        "Legacy add-to-cart pod id. Passed through for Builder editor parity.",
    },
    ...recsOptionInputs,
  ],
} satisfies RegisteredComponent;
